<template lang="pug">
    div.popup(:class="{ hidden: !isVisible }"
                :style="{ width: popupWidth + 'px', height: popupHeight + 'px', left: popupLeft + 'px', top: popupTop + 'px', maxWidth: maxWidth + 'px', maxHeight: maxHeight + 'px' }")
        div.header(@mousedown="startDragging")
            v-icon.mr-2 mdi-drag
            slot(name="header" :style="slotProps.headerStyles")
            v-tooltip(top)
                template(v-slot:activator='{ on, attrs }')
                    span(v-bind="attrs" v-on="on" role="text")
                        v-icon(@click="handleClose") mdi-close
                span Close
        div.content(style="overflow-y: auto; overflow-x: hidden;")
            slot(name="body")
        v-icon.resize-handle.bottom-left(@mousedown="startResizing($event)") mdi-resize-bottom-right

</template>

<script>
import Vue from 'vue';

export default Vue.extend({
    name: 'PopupWindow',
    data() {
        return {
            isDragging: false,
            isResizing: false,
            startX: 0,
            startY: 0,
            localStartWidth: 0,
            localStartHeight: 0,
            popupWidth: 400,
            popupHeight: 300,
            popupLeft: window.innerWidth / 2 - 200,
            popupTop: window.innerHeight / 2 - 150,
        };
    },
    methods: {
        closePopup() {
            if (this.onClose) {
                this.onClose();
            }
            this.$emit('update:isVisible', false);
        },
        handleClose(e) {
            if (e) e.stopPropagation();
            this.closePopup();
        },
        handleResize() {
            this.popupLeft = Math.max(0, Math.min(this.popupLeft, window.innerWidth - this.popupWidth));
            this.popupTop = Math.max(0, Math.min(this.popupTop, window.innerHeight - this.popupHeight));
        },
        init() {
            this.localStartWidth = this.startWidth;
            this.localStartHeight = this.startHeight;
            this.popupWidth = this.startWidth;
            this.popupHeight = this.startHeight;

            this.popupLeft += this.offsetLeft;
            this.popupTop += this.offsetTop;
        },
        mouseMoveHandler(e) {
            if (this.isDragging) {
                const newLeft = e.clientX - this.startX;
                const newTop = e.clientY - this.startY;
                this.popupLeft = Math.max(0, Math.min(newLeft, window.innerWidth - this.popupWidth));
                this.popupTop = Math.max(0, Math.min(newTop, window.innerHeight - this.popupHeight));
            }
        },
        mouseUpHandler() {
            this.isDragging = false;
            this.isResizing = false;
            document.body.style.cursor = 'default';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', this.mouseMoveHandler);
            document.removeEventListener('mousemove', this.resizeMoveHandler);
            document.removeEventListener('mouseup', this.mouseUpHandler);

            this.$nextTick(() => {
                document.body.style.cursor = '';
            });
        },
        resetState() {
            this.isDragging = false;
            this.isResizing = false;
        },
        resizeMoveHandler(e) {
            if (this.isResizing) {
                const originalXPos = this.popupLeft;
                const originalYPos = this.popupTop;
                let newWidth = this.localStartWidth;
                let newHeight = this.localStartHeight;

                newWidth = this.localStartWidth - (e.clientX - this.startX);
                newHeight = this.localStartHeight + (e.clientY - this.startY);
                if (newWidth < this.maxWidth) {
                    this.popupLeft = e.clientX; // Adjust left position
                } else {
                    newWidth = this.maxWidth;
                }
                if (newHeight < this.maxHeight) {
                    this.popupHeight = newHeight;
                } else {
                    newHeight = this.maxHeight;
                }

                // Ensure minimum width and height
                if (newWidth > this.minWidth) {
                    this.popupWidth = newWidth;
                } else if (newWidth <= this.minWidth) {
                    this.popupWidth = this.minWidth;
                    this.popupLeft = originalXPos; // Adjust position for left corners
                }

                if (newHeight > this.minHeight) {
                    this.popupHeight = newHeight;
                } else if (newHeight <= this.minHeight) {
                    this.popupHeight = this.minHeight;
                    // if (this.resizeDirection === 'top') {
                    //     this.popupTop = originalYPos; // Adjust position for top corners
                    // }
                }

                // Ensure popup does not go outside of the browser bounds after resizing
                this.popupLeft = Math.max(0, Math.min(this.popupLeft, window.innerWidth - this.popupWidth));
                this.popupTop = Math.max(0, Math.min(this.popupTop, window.innerHeight - this.popupHeight));
            }
        },
        startDragging(e) {
            this.isDragging = true;
            this.startX = e.clientX - this.popupLeft;
            this.startY = e.clientY - this.popupTop;
            document.body.style.userSelect = 'none'; // Prevent text selection
            document.addEventListener('mousemove', this.mouseMoveHandler);
            document.addEventListener('mouseup', this.mouseUpHandler);
        },
        startResizing(e) {
            this.isResizing = true;
            this.startX = e.clientX;
            this.startY = e.clientY;
            this.localStartWidth = this.popupWidth;
            this.localStartHeight = this.popupHeight;

            document.body.style.userSelect = 'none'; // Prevent text selection
            document.addEventListener('mousemove', this.resizeMoveHandler);
            document.addEventListener('mouseup', this.mouseUpHandler);
        },
    },
    props: {
        isVisible: {
            type: Boolean,
            required: true,
        },
        maxWidth: {
            type: Number,
            default: 1000,
        },
        maxHeight: {
            type: Number,
            default: 800,
        },
        minWidth: {
            type: Number,
            default: 200,
        },
        minHeight: {
            type: Number,
            default: 150,
        },
        offsetLeft: {
            type: Number,
            default: 0,
        },
        offsetTop: {
            type: Number,
            default: 0,
        },
        onClose: {
            type: Function,
            required: true,
        },
        slotProps: {
            type: Object,
            default: () => ({}),
        },
        startWidth: {
            type: Number,
            default: 400,
        },
        startHeight: {
            type: Number,
            default: 300,
        },
    },
    created() {
        this.init();
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('mousemove', this.mouseMoveHandler);
        document.removeEventListener('mousemove', this.resizeMoveHandler);
        document.removeEventListener('mouseup', this.mouseUpHandler);
    }
});
</script>

<style scoped>
body {
    margin: 0;
}

.popup {
    position: fixed;
    border: 1px solid var(--lt-gray);
    border-radius: 5px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;
}

.header {
    background: var(--off-white);
    padding: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: move;
}

.content {
    flex: 1;
    overflow-y: auto; /* Enable vertical scrolling */
    overflow-x: hidden; /* Disable horizontal scrolling */
    height: 100%;
}

.hidden {
    display: none;
}

.close {
    cursor: pointer;
    font-size: 20px;
}

.resize-handle {
    min-width: 10px;
    min-height: 10px;
    position: absolute;

    &.bottom-left {
        position: absolute;
        transform: rotate(90deg);
        left: 0;
        bottom: 0;
        cursor: nesw-resize;
    }
}

</style>